import { apiFetch } from './apiFetch';
import type {
  CreatePettyCashExpenseRequest,
  CreatePettyCashLoadRequest,
  CreatePettyCashRequest,
  ListPettyCashMovementsQuery,
  PettyCash,
  PettyCashAvailable,
  PettyCashCategory,
  PettyCashLoadReceipt,
  PettyCashMovement,
  PettyCashState,
  UpdatePettyCashRequest,
  UpsertPettyCashCategoryRequest,
} from '@deposits/shared';

export interface CreatedPettyCashMovement {
  id: number;
  movement_number: number;
  amount: number;
  pdf_status: 'pending' | 'ready' | 'failed';
}

/** The selected project's petty cash with its live state; null = not opened yet. */
export async function fetchPettyCash(projectId: number | null): Promise<PettyCashState | null> {
  const qs = projectId != null ? `?project_id=${projectId}` : '';
  return apiFetch<PettyCashState | null>(`/petty-cash${qs}`);
}

export async function createPettyCash(body: CreatePettyCashRequest): Promise<PettyCash> {
  return apiFetch<PettyCash>('/petty-cash', { method: 'POST', body: JSON.stringify(body) });
}

export async function updatePettyCash(id: number, body: UpdatePettyCashRequest): Promise<PettyCash> {
  return apiFetch<PettyCash>(`/petty-cash/${id}`, { method: 'PUT', body: JSON.stringify(body) });
}

export async function fetchPettyCashAvailable(id: number): Promise<PettyCashAvailable> {
  return apiFetch<PettyCashAvailable>(`/petty-cash/${id}/available`);
}

export async function fetchPettyCashMovements(
  id: number,
  q: ListPettyCashMovementsQuery,
): Promise<PettyCashMovement[]> {
  const params = new URLSearchParams({ from: q.from, to: q.to });
  return apiFetch<PettyCashMovement[]>(`/petty-cash/${id}/movements?${params.toString()}`);
}

export async function fetchPettyCashMovement(
  id: number,
): Promise<{ movement: PettyCashMovement; receipts: PettyCashLoadReceipt[] }> {
  return apiFetch(`/petty-cash/movements/${id}`);
}

export async function createPettyCashLoad(
  id: number,
  body: CreatePettyCashLoadRequest,
): Promise<CreatedPettyCashMovement> {
  return apiFetch(`/petty-cash/${id}/loads`, { method: 'POST', body: JSON.stringify(body) });
}

export async function createPettyCashExpense(
  id: number,
  body: CreatePettyCashExpenseRequest,
): Promise<CreatedPettyCashMovement> {
  return apiFetch(`/petty-cash/${id}/expenses`, { method: 'POST', body: JSON.stringify(body) });
}

export async function cancelPettyCashMovement(id: number, reason: string): Promise<void> {
  await apiFetch<void>(`/petty-cash/movements/${id}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function fetchPettyCashCategories(): Promise<PettyCashCategory[]> {
  return apiFetch<PettyCashCategory[]>('/petty-cash/categories');
}

export async function createPettyCashCategory(body: UpsertPettyCashCategoryRequest): Promise<PettyCashCategory> {
  return apiFetch('/petty-cash/categories', { method: 'POST', body: JSON.stringify(body) });
}

export async function updatePettyCashCategory(
  id: number,
  body: UpsertPettyCashCategoryRequest,
): Promise<PettyCashCategory> {
  return apiFetch(`/petty-cash/categories/${id}`, { method: 'PUT', body: JSON.stringify(body) });
}
